import React, { useEffect, useState } from 'react';
import {
    listarEventos,
    listarEventosPorRango,
    listarEventosPorFecha,
    cancelarEvento,
    deshacerCancelacion
} from '../services/eventoService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale/es';

registerLocale('es', es);

const ListadoEventos = () => {
    const [eventos, setEventos] = useState([]);
    const [fechaDesde, setFechaDesde] = useState(null);
    const [fechaHasta, setFechaHasta] = useState(null);
    const [filtroTematica, setFiltroTematica] = useState('');
    const [filtroFecha, setFiltroFecha] = useState(null);
    const [mensaje, setMensaje] = useState('');

    const dateFormat = (d) => {
        if (!d) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatearFechaLatina = (isoString) => {
        if (!isoString) return '';
        const [fecha, hora] = isoString.split('T');
        const [year, month, day] = fecha.split('-');
        const [hour, minute] = hora.split(':');
        return `${day}/${month}/${year} ${hour}:${minute}`;
    };

    const fetchTodosEventos = async () => {
        try {
            const response = await listarEventos();
            let filtrados = response.data;
            if (filtroTematica) {
                filtrados = filtrados.filter(ev =>
                    ev.tematica?.toLowerCase().includes(filtroTematica.toLowerCase())
                );
            }
            setEventos(filtrados);
        } catch (err) {
            setMensaje(err?.response?.data?.mensaje || 'Error al listar todos los eventos');
        }
    };

    const fetchEventosPorFecha = async () => {
        if (!filtroFecha) return;
        try {
            const response = await listarEventosPorFecha(dateFormat(filtroFecha));
            setEventos(response.data);
        } catch (err) {
            setMensaje(err?.response?.data?.mensaje || 'Error al listar eventos por fecha');
        }
    };

    const fetchEventosPorRango = async () => {
        try {
            const desde = fechaDesde ? dateFormat(fechaDesde) : '1900-01-01';
            const hasta = fechaHasta ? dateFormat(fechaHasta) : '2100-12-31';
            const response = await listarEventosPorRango(desde, hasta);
            let filtrados = response.data;
            if (filtroTematica) {
                filtrados = filtrados.filter(ev =>
                    ev.tematica?.toLowerCase().includes(filtroTematica.toLowerCase())
                );
            }
            setEventos(filtrados);
        } catch (err) {
            setMensaje(err?.response?.data?.mensaje || 'Error al listar eventos por rango');
        }
    };

    useEffect(() => {
        fetchEventosPorRango();
    }, [fechaDesde, fechaHasta, filtroTematica]);

    const handleCancelar = async (id) => {
        try {
            await cancelarEvento(id);
            fetchEventosPorRango();
        } catch (err) {
            setMensaje(err?.response?.data?.mensaje || 'Error al cancelar');
        }
    };

    const handleDeshacer = async (id) => {
        try {
            await deshacerCancelacion(id);
            fetchEventosPorRango();
        } catch (err) {
            setMensaje(err?.response?.data?.mensaje || 'Error al deshacer');
        }
    };

    return (
        <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Listado de Eventos</h3>

            <div className="flex flex-wrap gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-500">Desde:</label>
                    <DatePicker selected={fechaDesde} onChange={(d) => setFechaDesde(d)} locale="es" dateFormat="dd/MM/yyyy" placeholderText="Desde"
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-500">Hasta:</label>
                    <DatePicker selected={fechaHasta} onChange={(d) => setFechaHasta(d)} locale="es" dateFormat="dd/MM/yyyy" placeholderText="Hasta"
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-500">Temática:</label>
                    <input type="text" value={filtroTematica} onChange={(e) => setFiltroTematica(e.target.value)} placeholder="Filtrar por temática"
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-500">Fecha específica:</label>
                    <div className="flex gap-2">
                        <DatePicker selected={filtroFecha} onChange={(d) => setFiltroFecha(d)} locale="es" dateFormat="dd/MM/yyyy" placeholderText="Fecha"
                            className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        <button onClick={fetchEventosPorFecha} className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition-colors">Buscar</button>
                    </div>
                </div>
                <div className="flex items-end">
                    <button onClick={fetchTodosEventos} className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors">Ver todos</button>
                </div>
            </div>

            {mensaje && <p className="text-sm text-red-600">{mensaje}</p>}

            {eventos.length === 0 && <p className="text-sm text-gray-500">No se encontraron eventos</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {eventos.map((evento) => (
                    <div key={evento.fechaHoraEvento} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                        <div className="bg-primary-600 text-white px-4 py-2 font-medium text-sm">
                            {evento.correoResponsable || evento.telefonoResponsable}
                        </div>
                        <div className="p-4 space-y-1.5 text-sm text-gray-600">
                            <p><strong className="text-gray-900">Fecha:</strong> {formatearFechaLatina(evento.fechaHoraEvento)}</p>
                            <p><strong className="text-gray-900">Precio:</strong> ${evento.precioTotalDelEvento}</p>
                            <p><strong className="text-gray-900">Seña pagada:</strong> ${evento.senaPagada}</p>
                            <p><strong className="text-gray-900">Invitados:</strong> {evento.cantidadInvitados}</p>
                            <p><strong className="text-gray-900">Cliente:</strong> {evento.correoResponsable}</p>
                            <p><strong className="text-gray-900">Teléfono:</strong> {evento.telefonoResponsable}</p>
                            <p><strong className="text-gray-900">Talle cumpleañero:</strong> {evento.talleCumplaniero}</p>
                            <p><strong className="text-gray-900">Menores:</strong> {evento.asistenciaMenores ? 'Sí' : 'No'}</p>
                            <p><strong className="text-gray-900">Temática:</strong> {evento.tematica}</p>
                            <p><strong className="text-gray-900">Cancelado:</strong> {evento.cancelado ? 'Sí' : 'No'}</p>
                            {!evento.cancelado ? (
                                <button className="mt-2 px-3 py-1.5 bg-danger-600 text-white text-xs rounded hover:bg-danger-700 transition-colors" onClick={() => handleCancelar(evento.fechaHoraEvento)}>Cancelar</button>
                            ) : (
                                <button className="mt-2 px-3 py-1.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors" onClick={() => handleDeshacer(evento.fechaHoraEvento)}>Deshacer</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ListadoEventos;
